import React, { useEffect, useState } from 'react';
import { Box, Slider, TextField, Typography } from '@mui/material';
import {
  ENGAGEMENT_CATALOG,
  confidencePostures,
  proactivenessLevels,
  upsellPostures,
  type ConfidencePosture,
  type ProactivenessLevel,
  type UpsellPosture,
} from 'skipify-types';

import { ConfigSection } from '../../../common';
import { useEllaDraft } from '../EllaDraftContext';
import { FORM_MAX_WIDTH, exampleBoxSx, selectionBoxSx } from '../ellaFormStyles';

const EXAMPLE_BOX_SX = exampleBoxSx();

const INTRUSIVENESS_MIN = ENGAGEMENT_CATALOG.intrusiveness.min;
const INTRUSIVENESS_MAX = ENGAGEMENT_CATALOG.intrusiveness.max;
const INTRUSIVENESS_LEVELS = Array.from(
  { length: INTRUSIVENESS_MAX - INTRUSIVENESS_MIN + 1 },
  (_, index) => INTRUSIVENESS_MIN + index,
);

/** MMS WidgetPositionSchema: whole-number pixel offsets (may be negative). */
const INTEGER_OFFSET_PATTERN = /^-?\d+$/;

const parseOffsetInput = (raw: string): number | undefined => {
  const trimmed = raw.trim();
  if (!trimmed) {
    return undefined;
  }
  if (!INTEGER_OFFSET_PATTERN.test(trimmed)) {
    return Number.NaN;
  }
  return Number(trimmed);
};

const postureSliderSx = {
  color: 'brand.main',
  height: 6,
  py: 0,
  '& .MuiSlider-thumb': {
    width: 20,
    height: 20,
    backgroundColor: '#FFFFFF',
    border: '1px solid',
    borderColor: '#BDBDBD',
    boxShadow: 'none',
    '&:hover, &.Mui-focusVisible, &.Mui-active': {
      boxShadow: 'none',
    },
  },
  '& .MuiSlider-rail': {
    opacity: 1,
    backgroundColor: '#E0E0E0',
  },
  '& .MuiSlider-track': {
    border: 'none',
    backgroundColor: 'brand.main',
  },
  '& .MuiSlider-mark': {
    display: 'none',
  },
  '&.Mui-disabled': {
    color: 'brand.main',
    opacity: 0.7,
  },
};

type PostureSliderProps<T extends string> = {
  ariaLabel: string;
  values: readonly T[];
  labels: Record<T, string>;
  value: T;
  readOnly: boolean;
  onChange: (value: T) => void;
};

/** Align first label to track start, last to track end, middle labels centered on their marks. */
const getMarkLabelTransform = (index: number, count: number) => {
  if (index === 0) {
    return 'translateX(0)';
  }
  if (index === count - 1) {
    return 'translateX(-100%)';
  }
  return 'translateX(-50%)';
};

const PostureSlider = <T extends string>({
  ariaLabel,
  values,
  labels,
  value,
  readOnly,
  onChange,
}: PostureSliderProps<T>) => {
  const index = Math.max(values.indexOf(value), 0);
  const lastIndex = values.length - 1;

  return (
    <Box sx={{ maxWidth: FORM_MAX_WIDTH, mb: 2.5 }}>
      <Slider
        aria-label={ariaLabel}
        value={index}
        min={0}
        max={lastIndex}
        step={1}
        marks
        disabled={readOnly}
        onChange={(_e, nextValue) => {
          const nextIndex = Array.isArray(nextValue) ? nextValue[0] : nextValue;
          const next = values[nextIndex];
          if (next) {
            onChange(next);
          }
        }}
        sx={postureSliderSx}
      />
      <Box
        aria-hidden
        sx={{
          position: 'relative',
          height: 24,
          mt: 0.5,
        }}
      >
        {values.map((level, levelIndex) => {
          const isSelected = levelIndex === index;
          return (
            <Typography
              key={level}
              component="span"
              sx={{
                position: 'absolute',
                left: `${(levelIndex / lastIndex) * 100}%`,
                transform: getMarkLabelTransform(levelIndex, values.length),
                fontFamily: 'Poppins, sans-serif',
                fontSize: 14,
                fontWeight: isSelected ? 700 : 400,
                lineHeight: '24px',
                color: isSelected ? '#000000' : '#757575',
                whiteSpace: 'nowrap',
              }}
            >
              {labels[level]}
            </Typography>
          );
        })}
      </Box>
    </Box>
  );
};

type PostureSectionProps<T extends string> = {
  title: string;
  ariaLabel: string;
  exampleLabel: string;
  values: readonly T[];
  labels: Record<T, string>;
  descriptions: Record<T, string>;
  sectionDescription: string;
  value: T;
  readOnly: boolean;
  onChange: (value: T) => void;
};

const PostureSection = <T extends string>({
  title,
  ariaLabel,
  exampleLabel,
  values,
  labels,
  descriptions,
  sectionDescription,
  value,
  readOnly,
  onChange,
}: PostureSectionProps<T>) => (
  <ConfigSection title={title} description={sectionDescription} marginBottom={2}>
    <PostureSlider
      ariaLabel={ariaLabel}
      values={values}
      labels={labels}
      value={value}
      readOnly={readOnly}
      onChange={onChange}
    />
    <Typography variant="small" fontWeight="bold" sx={{ mb: 1 }}>
      {exampleLabel}: {labels[value]}
    </Typography>
    <Box sx={EXAMPLE_BOX_SX}>
      <Typography variant="small" color="text.secondary">
        {descriptions[value]}
      </Typography>
    </Box>
  </ConfigSection>
);

export const EngagementForm = (): JSX.Element => {
  const { draft, readOnly, isDirty, updateDraft } = useEllaDraft();
  const engagement = draft.engagement;
  const { intrusiveness } = engagement;
  const savedWidgetPosition = draft.productExperience.widgetPosition;

  const [rightInput, setRightInput] = useState(() =>
    savedWidgetPosition && Number.isInteger(savedWidgetPosition.right)
      ? String(savedWidgetPosition.right)
      : '',
  );
  const [bottomInput, setBottomInput] = useState(() =>
    savedWidgetPosition && Number.isInteger(savedWidgetPosition.bottom)
      ? String(savedWidgetPosition.bottom)
      : '',
  );

  // After hub SaveBar save/cancel, resync inputs from the committed draft.
  useEffect(() => {
    if (isDirty) {
      return;
    }
    const wp = draft.productExperience.widgetPosition;
    setRightInput(wp && Number.isInteger(wp.right) ? String(wp.right) : '');
    setBottomInput(wp && Number.isInteger(wp.bottom) ? String(wp.bottom) : '');
  }, [isDirty, draft.productExperience.widgetPosition]);

  const setEngagement = <K extends keyof typeof engagement>(
    key: K,
    value: (typeof engagement)[K],
  ) => {
    if (readOnly) {
      return;
    }
    updateDraft((prev) => ({
      ...prev,
      engagement: {
        ...prev.engagement,
        [key]: value,
      },
    }));
  };

  const setWidgetPositionInputs = (nextRight: string, nextBottom: string) => {
    if (readOnly) {
      return;
    }
    setRightInput(nextRight);
    setBottomInput(nextBottom);

    const right = parseOffsetInput(nextRight);
    const bottom = parseOffsetInput(nextBottom);

    updateDraft((prev) => {
      const productExperience = { ...prev.productExperience };
      if (right === undefined && bottom === undefined) {
        delete productExperience.widgetPosition;
      } else if (Number.isInteger(right) && Number.isInteger(bottom)) {
        productExperience.widgetPosition = { right: right!, bottom: bottom! };
      } else {
        // Incomplete/invalid pair — keep Save disabled via isDraftValid.
        productExperience.widgetPosition = { right: Number.NaN, bottom: Number.NaN };
      }
      return { ...prev, productExperience };
    });
  };

  const rightError =
    rightInput.trim().length > 0 && !INTEGER_OFFSET_PATTERN.test(rightInput.trim());
  const bottomError =
    bottomInput.trim().length > 0 && !INTEGER_OFFSET_PATTERN.test(bottomInput.trim());
  const pairError =
    (rightInput.trim().length > 0 && bottomInput.trim().length === 0) ||
    (bottomInput.trim().length > 0 && rightInput.trim().length === 0);

  return (
    <Box
      component="form"
      id="ella-engagement-form"
      data-testid="ella-engagement-form"
      onSubmit={(e) => e.preventDefault()}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 5,
      }}
    >
      <ConfigSection
        title="Widget position"
        description="Optional. Pixel offset for the chat widget from the bottom-right corner of the viewport. Leave blank to use the default position. Values may be negative."
        marginBottom={2}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            maxWidth: FORM_MAX_WIDTH,
          }}
        >
          <TextField
            fullWidth
            label="Right (px)"
            type="number"
            value={rightInput}
            onChange={(e) => setWidgetPositionInputs(e.target.value, bottomInput)}
            disabled={readOnly}
            error={rightError || pairError}
            helperText={
              rightError
                ? 'Enter a whole number'
                : pairError && rightInput.trim().length === 0
                  ? 'Required when bottom is set'
                  : undefined
            }
            inputProps={{
              'aria-label': 'Widget position right offset',
              step: 1,
            }}
          />
          <TextField
            fullWidth
            label="Bottom (px)"
            type="number"
            value={bottomInput}
            onChange={(e) => setWidgetPositionInputs(rightInput, e.target.value)}
            disabled={readOnly}
            error={bottomError || pairError}
            helperText={
              bottomError
                ? 'Enter a whole number'
                : pairError && bottomInput.trim().length === 0
                  ? 'Required when right is set'
                  : undefined
            }
            inputProps={{
              'aria-label': 'Widget position bottom offset',
              step: 1,
            }}
          />
        </Box>
      </ConfigSection>

      <ConfigSection
        title="Intrusiveness"
        description={ENGAGEMENT_CATALOG.intrusiveness.sectionDescription}
        marginBottom={2}
      >
        <Box
          sx={{ display: 'flex', gap: '10px', mb: 2.5 }}
          role="radiogroup"
          aria-label="Intrusiveness"
        >
          {INTRUSIVENESS_LEVELS.map((level) => {
            const isSelected = intrusiveness === level;
            return (
              <Box
                key={level}
                component="button"
                type="button"
                role="radio"
                aria-checked={isSelected}
                aria-label={`Intrusiveness level ${level}: ${ENGAGEMENT_CATALOG.intrusiveness.labels[level]}`}
                disabled={readOnly}
                onClick={() => setEngagement('intrusiveness', level)}
                sx={{
                  ...selectionBoxSx(isSelected, readOnly),
                  width: 80,
                  height: 52,
                  fontSize: 14,
                  fontWeight: 400,
                  lineHeight: '27px',
                  letterSpacing: 0,
                }}
              >
                {level}
              </Box>
            );
          })}
        </Box>
        <Typography variant="small" fontWeight="bold" sx={{ mb: 1 }}>
          Intrusiveness example:{' '}
          {
            ENGAGEMENT_CATALOG.intrusiveness.labels[
              intrusiveness as keyof typeof ENGAGEMENT_CATALOG.intrusiveness.labels
            ]
          }{' '}
          ({intrusiveness})
        </Typography>
        <Box sx={EXAMPLE_BOX_SX}>
          <Typography variant="small" color="text.secondary">
            {
              ENGAGEMENT_CATALOG.intrusiveness.descriptions[
                intrusiveness as keyof typeof ENGAGEMENT_CATALOG.intrusiveness.descriptions
              ]
            }
          </Typography>
        </Box>
      </ConfigSection>

      <PostureSection
        title="Upsell Posture"
        ariaLabel="Upsell posture"
        exampleLabel="Upsell posture"
        values={upsellPostures}
        labels={ENGAGEMENT_CATALOG.upsellPostures.labels}
        descriptions={ENGAGEMENT_CATALOG.upsellPostures.descriptions}
        sectionDescription={ENGAGEMENT_CATALOG.upsellPostures.sectionDescription}
        value={engagement.upsellPosture as UpsellPosture}
        readOnly={readOnly}
        onChange={(value) => setEngagement('upsellPosture', value)}
      />
      <PostureSection
        title="Confidence Posture"
        ariaLabel="Confidence posture"
        exampleLabel="Confidence posture"
        values={confidencePostures}
        labels={ENGAGEMENT_CATALOG.confidencePostures.labels}
        descriptions={ENGAGEMENT_CATALOG.confidencePostures.descriptions}
        sectionDescription={ENGAGEMENT_CATALOG.confidencePostures.sectionDescription}
        value={engagement.confidencePosture as ConfidencePosture}
        readOnly={readOnly}
        onChange={(value) => setEngagement('confidencePosture', value)}
      />
      <PostureSection
        title="Proactiveness"
        ariaLabel="Proactiveness"
        exampleLabel="Proactiveness"
        values={proactivenessLevels}
        labels={ENGAGEMENT_CATALOG.proactivenessLevels.labels}
        descriptions={ENGAGEMENT_CATALOG.proactivenessLevels.descriptions}
        sectionDescription={ENGAGEMENT_CATALOG.proactivenessLevels.sectionDescription}
        value={engagement.proactiveness as ProactivenessLevel}
        readOnly={readOnly}
        onChange={(value) => setEngagement('proactiveness', value)}
      />
    </Box>
  );
};
